import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import {
  alpha,
  Box,
  IconButton,
  InputBase,
  Stack,
  Typography,
} from "@mui/material";
import { Spacer, Toolbar } from "common";
import I18n from "i18n-js";
import { useState } from "react";
import { useFormEditor } from "./FormEditorDataProvider";
import Row from "./Row";
import { BlockItem } from "./types";
export interface BlockProps {
  blockIndex: number;
  block: BlockItem;
}
export default function Block({ blockIndex, block }: BlockProps) {
  const {
    handleDeleteBlock,
    handleMoveBlockByDir,
    handleBlockChange,
    value,
    handleAddRows,
    handleAddFields,
  } = useFormEditor();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState<string | null>(block.name);

  // Smart header logic: hide block header if there's only one row with one field
  const hasOnlyOneRow = block.rows?.length === 1;
  const hasOnlyOneField = hasOnlyOneRow && block.rows[0]?.fields?.length === 1;
  const shouldHideBlockHeader = hasOnlyOneField;
  const shouldHideRowHeader = hasOnlyOneRow; // Hide row header if there's only one row
  const shouldShowExtraActions = shouldHideRowHeader && !shouldHideBlockHeader; // Show extra actions when row header is hidden but block header is visible
  return (
    <Box
      sx={{
        flex: 1,
        //marginBottom: 2,
        borderColor: "primary.main",
        borderStyle: "dashed",
        borderWidth: shouldHideBlockHeader ? 0 : 1,
        borderRadius: 1,
        padding: shouldHideBlockHeader ? 0 : "3px",
        paddingTop: 0,
        backgroundColor: shouldHideBlockHeader
          ? "transparent"
          : theme => alpha(theme.palette.primary.main, 0.1),
      }}
    >
      {!shouldHideBlockHeader && (
        <Toolbar sx={{ paddingLeft: "4px" }}>
          {isEditing ? (
            <InputBase
              size="small"
              fullWidth
              value={name || ""}
              autoFocus
              sx={{
                color: "primary.main",
                flex: 1,
                backgroundColor: "background.default",
                verticalAlign: "middle",
                marginLeft: "-4px",

                //paddingLeft: 1,
                //paddingRight: 1,
                "& input": {
                  padding: "2px 4px",
                },
              }}
              onChange={evt =>
                setName(evt.target.value === "" ? null : evt.target.value)
              }
              onBlur={() => {
                handleBlockChange({ name: name as any }, blockIndex);
                setIsEditing(false);
              }}
            />
          ) : (
            <Toolbar onClick={() => setIsEditing(true)} sx={{ cursor: "text" }}>
              <Typography color="primary.main" variant="body1">
                {name}
              </Typography>
              <Spacer w={2} />
              <EditIcon
                fontSize="small"
                sx={{ color: "primary.main", fontSize: "1rem" }}
              />
            </Toolbar>
          )}
          <Spacer right />

          {/* Extra actions when row header is hidden but block header is visible */}
          {shouldShowExtraActions && (
            <>
              <IconButton
                title={I18n.t("FormEditor.addFieldToRowTitle")}
                size="small"
                onClick={() => handleAddFields(blockIndex, 0)} // Add to the first (and only) row
              >
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton
                title={I18n.t("FormEditor.addNewRow")}
                size="small"
                onClick={() => handleAddRows(blockIndex)}
              >
                <ViewStreamIcon fontSize="small" />
              </IconButton>
            </>
          )}

          <IconButton
            title={I18n.t("FormEditor.moveBlockDown")}
            size="small"
            onClick={() => {
              handleMoveBlockByDir(blockIndex, "down");
            }}
            disabled={blockIndex >= (value || [])?.length - 1}
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            title={I18n.t("FormEditor.moveBlockUp")}
            size="small"
            onClick={() => {
              handleMoveBlockByDir(blockIndex, "up");
            }}
            disabled={blockIndex < 1}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            title={I18n.t("FormEditor.removeBlock")}
            size="small"
            onClick={() => {
              handleDeleteBlock(blockIndex);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Toolbar>
      )}
      <Stack direction="column" spacing={2}>
        {block.rows?.map((row, rowIndex) => {
          return (
            <Row
              key={`${blockIndex}-${rowIndex}`}
              coordinates={{ blockIndex, rowIndex }}
              hideRowHeader={shouldHideRowHeader}
              blockActions={
                shouldHideBlockHeader
                  ? {
                      blockIndex,
                      handleDeleteBlock,
                      handleMoveBlockByDir,
                      totalBlocks: (value || []).length,
                      blockName: name,
                      isEditingBlockName: isEditing,
                      setIsEditingBlockName: setIsEditing,
                      handleBlockChange,
                      setBlockName: setName,
                      handleAddRows,
                      handleAddFields,
                    }
                  : undefined
              }
            />
          );
        })}
      </Stack>
    </Box>
  );
}
